import React from 'react'
import { Badge, Tag } from 'components/ds/Badge'
import Icon from 'components/ds/Icon'
import { NOT_REPORTED } from './fmcsaViewModel'
import { Card, Field } from './FmcsaSummary'
import styles from './FmcsaDetails.module.scss'

const cx = (...p) => p.filter(Boolean).join(' ')

const Basic = ({ basic }) => (
    <div className={cx(styles.basic, basic.exceeded && styles.exceeded)}>
        <div className={styles.basicHead}>
            <span>{basic.name}</span>
            {basic.exceeded && <Badge tone="watch">Over threshold</Badge>}
        </div>
        {basic.percentile != null
            ? (
                <div className={cx(styles.percentile, basic.exceeded && styles.exceeded)}>
                    <strong>{basic.percentile}</strong> percentile
                </div>
            )
            : <span className={styles.notEnough}>Percentile not published</span>}
        {basic.threshold != null && basic.percentile != null && (
            <>
                <div className={styles.gauge} role="img"
                     aria-label={`${basic.name}: ${basic.percentile ?? 'no'} percentile, threshold ${basic.threshold}`}>
                    {basic.percentile != null && (
                        <div className={cx(styles.gaugeFill, basic.exceeded && styles.watch)}
                             style={{ width: `${Math.min(100, basic.percentile)}%` }} />
                    )}
                    <span className={styles.gaugeTick} style={{ left: `${Math.min(100, basic.threshold)}%` }} />
                </div>
            </>
        )}
        {basic.threshold != null && <span className={styles.gaugeNotes}>Intervention threshold {basic.threshold}%</span>}
        <div className={styles.basicRows}>
            {basic.rows.map(r => (
                <div key={r.label}>
                    <span>{r.label}</span>
                    <span className={cx(r.value === NOT_REPORTED && styles.muted, r.alert && styles.alert)}>{r.value}</span>
                </div>
            ))}
        </div>
    </div>
)

const valueClass = row => cx(row.value === NOT_REPORTED && styles.muted, row.alert && styles.alert)

// Used when FMCSA publishes no percentile for any category: gauges would all be empty.
// Table on wide drawers, stacked list on narrow ones (switched in CSS).
const BasicsCompact = ({ basics }) => (
    <>
        <table className={styles.basicsTable}>
            <thead>
                <tr>
                    <th>Category</th>
                    <th>Measure</th>
                    <th>Inspections w/ violations</th>
                    <th>Total violations</th>
                    <th>Serious (12 mo)</th>
                    <th>Threshold</th>
                </tr>
            </thead>
            <tbody>
                {basics.map(b => (
                    <tr key={b.name}>
                        <td>{b.name}</td>
                        {b.rows.map(r => <td key={r.label} className={valueClass(r)}>{r.value}</td>)}
                        <td>{b.threshold != null ? `${b.threshold}%` : NOT_REPORTED}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        <div className={styles.basicsList}>
            {basics.map(b => (
                <div key={b.name} className={styles.basic}>
                    <div className={styles.basicHead}>
                        <span>{b.name}</span>
                        {b.threshold != null && <span className={styles.gaugeNotes}>Threshold {b.threshold}%</span>}
                    </div>
                    <div className={styles.basicRows}>
                        {b.rows.map(r => (
                            <div key={r.label}>
                                <span>{r.label}</span>
                                <span className={valueClass(r)}>{r.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    </>
)

const TagList = ({ items }) => (
    <div className={styles.tags}>
        {items.length > 0
            ? items.map(t => <Tag key={t}>{t}</Tag>)
            : <span className={cx(styles.muted, styles.fieldValue)}>{NOT_REPORTED}</span>}
    </div>
)

const ExtensiveFmcsaDetails = ({ extensive, legalName, dot }) => (
    <div className={styles.body}>
        {(legalName || dot) && (
            <div className={styles.subject}>
                {legalName && <span className={styles.subjectName}>{legalName}</span>}
                {dot && <span className={styles.muted}>DOT {dot}</span>}
            </div>
        )}

        <div className={styles.grid}>
            <Card title="MC numbers">
                {extensive.mcRows.length > 0
                    ? (
                        <div className={styles.mcTable}>
                            <span className={styles.eyebrow}>Docket</span>
                            <span className={styles.eyebrow}>DOT</span>
                            <span className={styles.eyebrow}>Prefix</span>
                            {extensive.mcRows.map((m, i) => (
                                <React.Fragment key={i}>
                                    <span>{m.docket}</span><span>{m.dot}</span><span>{m.prefix}</span>
                                </React.Fragment>
                            ))}
                        </div>
                    )
                    : (
                        <div className={styles.empty}>
                            <strong>No MC numbers on file</strong>
                            <span>{dot ? `FMCSA lists no docket for DOT ${dot}.` : 'FMCSA lists no docket for this carrier.'}</span>
                        </div>
                    )}
            </Card>

            <Card title="Authority information">
                {extensive.authorities.length === 0 && <span className={styles.muted}>{NOT_REPORTED}</span>}
                {extensive.authorities.map((a, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <div className={styles.divider} />}
                        <Field label="Authority status" value={a.status} />
                        <Field label="Docket" value={a.docket} />
                        <span className={styles.eyebrow}>Authorized for</span>
                        <div className={styles.authorizedFor}>
                            {a.authorizedFor.map(f => (
                                <div key={f.label} className={cx(styles.authorizedItem, f.value === 'Yes' && styles.yes)}>
                                    <Icon name={f.value === 'Yes' ? 'check-circle' : 'minus-circle'} size={16} />
                                    <span>{f.label}</span>
                                    <span>{f.value || NOT_REPORTED}</span>
                                </div>
                            ))}
                        </div>
                    </React.Fragment>
                ))}
            </Card>

            <Card title="BASICs" className={styles.full}>
                {extensive.basics.length === 0
                    ? <span className={styles.muted}>{NOT_REPORTED}</span>
                    : (
                        <>
                            <span className={styles.verdictDetail}>{extensive.basicsSummary}</span>
                            {extensive.basicsCompact
                                ? <BasicsCompact basics={extensive.basics} />
                                : (
                                    <div className={styles.basicsGrid}>
                                        {extensive.basics.map(b => <Basic key={b.name} basic={b} />)}
                                    </div>
                                )}
                        </>
                    )}
            </Card>

            <Card title="Cargo carried">
                <TagList items={extensive.cargo} />
            </Card>

            <Card title="Operation classification">
                <TagList items={extensive.operations} />
            </Card>

            <Card title="Out of service" className={styles.full}>
                {extensive.oosBadge && <div><Badge tone={extensive.oosBadge.tone}>{extensive.oosBadge.label}</Badge></div>}
                {extensive.oosRows.length > 0
                    ? extensive.oosRows.map((r, i) => <Field key={`${r.label}-${i}`} label={r.label} value={r.value} />)
                    : <span className={styles.verdictDetail}>FMCSA returned no out-of-service details.</span>}
            </Card>
        </div>
    </div>
)

export default ExtensiveFmcsaDetails
